import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import AskForAccess from "./AskForAccess";
import { decisionReason, needText } from "./refusalWords";

/**
 * A refused action, said once for every screen (accounts.md GS-8, redesign R3-5 `blocked`): for a
 * permission one lacks, what is missing, the groups that give it and who can add one, as the
 * remedy names them ("an administrator" where it names nobody), and Ask where one may; for any
 * other refusal, its words. `children` are what the decision opens, such as a form or a page:
 * shown in its place once it is allowed, so a screen never branches on the decision itself.
 */
export default function BlockedState({ decision, children = null }: { decision: Decision; children?: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { remedy } = decision;
  if (decision.refusal === null) return children;
  if (decision.refusal !== "missing_permission" || remedy === null) return <p className="text-sm">{decisionReason(t, decision)}</p>;
  const list = (names: readonly string[], type: "conjunction" | "disjunction") => new Intl.ListFormat(i18n.resolvedLanguage, { type }).format(names);
  const who = [
    remedy.holders.length === 0 ? null : t("refusals.blocked.haveIt", { groups: list(remedy.holders, "conjunction") }),
    remedy.deciders.length === 0
      ? t("refusals.blocked.administrator")
      : t("refusals.blocked.canAdd", { count: remedy.deciders.length, names: list(remedy.deciders, "disjunction") }),
  ].filter((line) => line !== null);
  return (
    <div className="space-y-2 text-sm">
      <p className="font-semibold">{t("refusals.blocked.need", { permission: needText(t, remedy.needs) })}</p>
      <p className="app-text-muted">{who.join(" ")}</p>
      {remedy.canRequest ? <AskForAccess projectId={decision.projectId} needs={remedy.needs} /> : null}
    </div>
  );
}
